// Copyright © 2025 Ory Corp
// SPDX-License-Identifier: Apache-2.0

import { Configuration, OAuth2Api } from "@ory/hydra-client-fetch"

const baseOptions: any = {}

if (process.env.MOCK_TLS_TERMINATION) {
    baseOptions.headers = { "X-Forwarded-Proto": "https" }
}

const configuration = new Configuration({
    basePath: process.env.HYDRA_ADMIN_URL || "http://localhost:4445",
    accessToken: process.env.ORY_API_KEY || process.env.REACT_APP_MOCK_ORY_PAT,
    headers: baseOptions.headers,
})

const hydraAdmin = new OAuth2Api(configuration)

export { hydraAdmin }

export class Oauth2Service {

    static async auth(challenge: string): Promise<string> {
        const loginRequest = await hydraAdmin
            .getOAuth2LoginRequest({
                loginChallenge: challenge,
            })

        const accept = await hydraAdmin.acceptOAuth2LoginRequest(
            {
                loginChallenge: challenge,
                acceptOAuth2LoginRequest: {
                    // Subject is an alias for user ID. A subject can be a random string, a UUID, an email address, ....
                    subject: "foo@bar.com",

                    // This tells hydra to remember the browser and automatically authenticate the user in future requests. This will
                    // set the "skip" parameter in the other route to true on subsequent requests!
                    remember: true,

                    // When the session expires, in seconds. Set this to 0 so it will never expire.
                    remember_for: 36000,

                    // Sets which "level" (e.g. 2-factor authentication) of authentication the user has. The value is really arbitrary
                    // and optional. In the context of OpenID Connect, a value of 0 indicates the lowest authorization level.
                    // acr: '0',
                    //
                    // If the environment variable CONFORMITY_FAKE_CLAIMS is set we are assuming that
                    // the app is built for the automated OpenID Connect Conformity Test Suite. You
                    // can peak inside the code for some ideas, but be aware that all data is fake
                    // and this only exists to fake a login system which works in accordance to OpenID Connect.
                    //
                    // If that variable is not set, the ACR value will be set to the default passed here ('0')
                },
            }
        )
        console.log("accept to: ", accept.redirect_to)
        return Promise.resolve(accept.redirect_to)
    }

    static async consent(challenge: string): Promise<string> {
        const consentRequest = await hydraAdmin
            .getOAuth2ConsentRequest({
                consentChallenge: challenge,
            })


        if (consentRequest.skip || (consentRequest.client as any)?.skip_consent) {

            const accept = await hydraAdmin
                .acceptOAuth2ConsentRequest({
                    consentChallenge: challenge,
                    acceptOAuth2ConsentRequest: {
                        // We can grant all scopes that have been requested - hydra already checked for us that no additional scopes
                        // are requested accidentally.
                        grant_scope: consentRequest.requested_scope,

                        // ORY Hydra checks if requested audiences are allowed by the client, so we can simply echo this.
                        grant_access_token_audience:
                            consentRequest.requested_access_token_audience,

                        // The session allows us to set session data for id and access tokens
                        session: {
                            // This data will be available when introspecting the token. Try to avoid sensitive information here,
                            // unless you limit who can introspect tokens.
                            // accessToken: { foo: 'bar' },
                            // This data will be available in the ID token.
                            // idToken: { baz: 'bar' },
                        },
                    },
                })
            console.log("accept to: ", accept.redirect_to)
            return Promise.resolve(accept.redirect_to)
        }
        return Promise.reject("no consent")
    }
}